/** @jsx jsx */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { css, jsx } from '@emotion/core';
import { CircularProgress, Typography } from '@material-ui/core';

import { content } from 'actions/content.actions';
import {
  appConfigSelector,
  appOptionsSelector,
  isAuthenticatedSelector,
  userInfosSelector,
} from 'reducers/app.reducer';
import {
  currentContentSelector,
  contentStatusSelector,
} from 'reducers/content.reducer';
import { ICustomTheme, ContentType, StoreOptionsType } from 'interfaces';
import { CustomButton, CustomModal, Link, Text, Title } from 'components';
import { updateUserConditionsAndTerms } from 'actions/app.actions';
import { LOADING_STATUS, STATUS } from 'constants/status';
import { isHomepage as isHomepagePath } from 'core/utils/is-homepage';
import {
  EventStatus,
  getPublicationStatusFromDate,
} from 'core/utils/date.helpers_ts';
import { checkHasOnlyNotRunningWidgetsBlocs } from 'utils';
import { ContentBlocks } from 'components/pages/Content';

const ContentWrapper = styled.div<{ theme: ICustomTheme }>(({ theme }) => ({
  flex: '1 0 auto',
  paddingBottom: theme.spacing(5),
}));
const NoContent = styled(Typography)<{ theme: ICustomTheme }>(({ theme }) => ({
  padding: theme.spacing(5),
}));

const cssLoaderContainer = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  minHeight: 200,
});

export const StyledCircularProgress = styled(CircularProgress)<{
  theme: ICustomTheme;
}>(({ theme }) => ({
  color: theme.colors.paletteFromBO.main,
}));

const Content = (): JSX.Element => {
  const { t } = useTranslation();
  // Chemin réel de la page : le helper est partagé avec useExternalRedirect et
  // avec le SCL, il ne connaît pas le basename du Router.
  const isHomepage = isHomepagePath(window.location.pathname);
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const status = useSelector(contentStatusSelector);
  const currentContent: ContentType | null = useSelector(
    currentContentSelector,
  );
  const pageTitle = currentContent?.title;
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const options: StoreOptionsType = useSelector(appOptionsSelector);
  const maxAppTitle =
    options.list?.find((option) => option.name === 'UI_META_TITLE')?.value ||
    'MAX Front Office';

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(content.request({ id: isHomepage ? 'homepage' : `${id}/fo` }));
    }
  }, [id, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const [openCGUModal, setOpenCGUModal] = useState(false);
  const { isCGUAccepted, id: userId } = useSelector(userInfosSelector);
  const { programUrl, userConditionsPath } = useSelector(appConfigSelector);

  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      (pageTitle && maxAppTitle && `${pageTitle} - ${maxAppTitle}`) ||
      'MAX Front Office';
    return () => {
      document.title = prevTitle;
    };
  }, [pageTitle, maxAppTitle]);

  useEffect(() => {
    if (isCGUAccepted) {
      setOpenCGUModal(false);
    } else {
      setOpenCGUModal(true);
    }
  }, [isCGUAccepted]);

  const handleClose = () => {
    if (userId) {
      dispatch(updateUserConditionsAndTerms.request(userId));
    }
  };

  if (status === LOADING_STATUS.PENDING || status === LOADING_STATUS.IDLE) {
    return (
      <ContentWrapper>
        <div css={cssLoaderContainer}>
          <StyledCircularProgress />
        </div>
      </ContentWrapper>
    );
  }

  // check if there are content blocs
  const hasContentBlocs = !!currentContent?.contentBlocs?.length;

  // check if the page is on published status
  const isPublishedPage = currentContent?.status === STATUS.PUBLISHED;

  // check if the page is on running publication status
  const isPageRunning =
    getPublicationStatusFromDate(
      currentContent?.startDate,
      currentContent?.endDate,
    ) === EventStatus.RUNNING;

  // check if the content blocs list only contains widgets content and if all of these widgets aren't in running status
  const hasOnlyNotRunningWidgetsBlocs: boolean =
    !!currentContent?.contentBlocs?.length &&
    checkHasOnlyNotRunningWidgetsBlocs(currentContent?.contentBlocs);

  if (
    !hasContentBlocs ||
    !isPublishedPage ||
    !isPageRunning ||
    hasOnlyNotRunningWidgetsBlocs
  ) {
    return (
      <ContentWrapper>
        <NoContent variant="h4">{t('core.no_content')}</NoContent>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <CustomModal open={openCGUModal} disableBackdropClick>
        <Title
          align="center"
          size="h4"
          isParagraph
          css={{ textTransform: 'uppercase' }}
        >
          {t('terms.modal.title')}
        </Title>
        <Text position="center" isParagraph>
          <Trans
            i18nKey="terms.modal.description"
            components={{
              linkToCGU: (
                <Link
                  color="primary"
                  decoration="underline"
                  isParagraph={false}
                  textCase="lower"
                  url={`${programUrl}${userConditionsPath}`}
                  weight="bold"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </Text>
        <Text align="center" isParagraph color="primary" weight="bold">
          {t('terms.modal.agreement')}
        </Text>
        <CustomButton btnType="button" color="primary" onClick={handleClose}>
          {t('terms.modal.i_agree')}
        </CustomButton>
      </CustomModal>
      {!!currentContent && hasContentBlocs && (
        <ContentBlocks contentBlocks={currentContent.contentBlocs!} />
      )}
    </ContentWrapper>
  );
};

export default Content;
